import React, { useEffect, useId, useRef, useState } from 'react';
import { multiPath, polygonPath, signedArea, splitPolygon } from './geometry.mjs';

export const SHAPE_COLORS = [
  { light: '#ffb25d', main: '#ff8950', edge: '#d16c39' },
  { light: '#90ded0', main: '#48bda8', edge: '#328d80' },
  { light: '#a4c6ff', main: '#76a1ee', edge: '#547cc5' },
  { light: '#ffdd79', main: '#f3c455', edge: '#cba03c' },
  { light: '#fda7ad', main: '#f17f91', edge: '#c76072' }
];

function markerPoint(multi) {
  // Area centroid of largest component. Hide the label on small slices.
  const ring = [...multi].sort((a, b) => Math.abs(signedArea(b[0])) - Math.abs(signedArea(a[0])))[0]?.[0];
  if (!ring) return [180, 160];
  let x = 0, y = 0, sum = 0;
  ring.forEach((p, i) => {
    const q = ring[(i + 1) % ring.length], c = p[0] * q[1] - q[0] * p[1];
    sum += c; x += (p[0] + q[0]) * c; y += (p[1] + q[1]) * c;
  });
  return sum ? [x / (3 * sum), y / (3 * sum)] : ring[0];
}

export const SliceShape = ({ id, color, path, result, preview }) => {
  if (!result) return <g filter={`url(#${id}-shadow)`}>
    <path data-testid="game-shape" d={path} fill={`url(#${id}-fill)`} stroke={color.edge} strokeWidth="0.65"/>
    {preview?.split.valid && <g data-testid="slice-preview" pointerEvents="none">
      <path d={multiPath(preview.split.selected)} fill="#ffffff" fillOpacity=".26" stroke="white" strokeOpacity=".85" strokeWidth="1.1" fillRule="evenodd"/>
      <path d={multiPath(preview.split.selected)} fill={`url(#${id}-hatch)`} fillRule="evenodd"/>
    </g>}
  </g>;
  return <g filter={`url(#${id}-shadow)`}>
    <path data-testid="slice-remainder" d={multiPath(result.remainder)} fill={`url(#${id}-fill)`} stroke={color.edge} strokeWidth=".65" fillRule="evenodd"/>
    <g data-testid="slice-selected" className="separated-piece" style={{ '--slice-x': `${result.normal[0] * 16}px`, '--slice-y': `${result.normal[1] * 16}px` }}>
      <path d={multiPath(result.selected)} fill={`url(#${id}-fill)`} stroke={color.edge} strokeWidth=".65" fillRule="evenodd"/>
      <path d={multiPath(result.selected)} fill="white" fillOpacity=".25" fillRule="evenodd"/>
    </g>
  </g>;
};

export function SliceSurface({ challenge, result, onCut, onInvalid, children, disabled = false }) {
  const svgRef = useRef(null), gesture = useRef(null), frame = useRef(null), latest = useRef(null);
  const [preview, setPreview] = useState(null);
  const [keyboard, setKeyboard] = useState(null);
  const [keyboardHint, setKeyboardHint] = useState(false);
  const id = useId().replace(/:/g, '');
  const color = SHAPE_COLORS[challenge.colorIndex];
  const path = polygonPath(challenge.polygon);
  const pointFromEvent = event => {
    const svg = svgRef.current;
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint(); point.x = event.clientX; point.y = event.clientY;
    const local = point.matrixTransform(matrix.inverse());
    return [local.x, local.y];
  };
  const clearGesture = () => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = null; gesture.current = null; latest.current = null;
    setPreview(null);
  };
  useEffect(() => {
    setPreview(null); setKeyboard(null); gesture.current = null;
    return () => { if (frame.current) cancelAnimationFrame(frame.current); };
  }, [challenge.seed, result, disabled]);
  const down = event => {
    if (disabled || result || gesture.current || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const point = pointFromEvent(event);
    if (!point) return;
    event.preventDefault();
    setKeyboard(null); setKeyboardHint(false);
    gesture.current = { start: point, id: event.pointerId, client: [event.clientX, event.clientY] };
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Synthetic pointer tests have no capture. */ }
  };
  const move = event => {
    if (disabled || !gesture.current || gesture.current.id !== event.pointerId) return;
    event.preventDefault();
    latest.current = pointFromEvent(event);
    if (!frame.current) frame.current = requestAnimationFrame(() => {
      frame.current = null;
      if (!gesture.current || !latest.current) return;
      const { start } = gesture.current, end = latest.current;
      if (Math.hypot(end[0] - start[0], end[1] - start[1]) < 2) return;
      setPreview({ start, end, split: splitPolygon(challenge.polygon, start, end) });
    });
  };
  const up = event => {
    if (disabled || !gesture.current || gesture.current.id !== event.pointerId || result) return;
    event.preventDefault();
    const { start, client } = gesture.current, end = pointFromEvent(event);
    const traveled = Math.hypot(event.clientX - client[0], event.clientY - client[1]);
    clearGesture();
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* Already released. */ }
    if (traveled < 8 || !end) { onInvalid(); return; }
    const split = splitPolygon(challenge.polygon, start, end);
    if (split.valid) onCut(split); else onInvalid();
  };
  const keyDown = event => {
    if (disabled || result) return;
    const relevant = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'q', 'Q', 'e', 'E', 'Enter', ' ', 'Escape'];
    if (!relevant.includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Escape') { setKeyboard(null); setPreview(null); return; }
    const current = keyboard || { x: 180, y: 160, angle: 0 };
    let next = { ...current }, step = event.shiftKey ? 1 : 5;
    if (event.key === 'ArrowUp') next.y -= step;
    if (event.key === 'ArrowDown') next.y += step;
    if (event.key === 'ArrowLeft') next.x -= step;
    if (event.key === 'ArrowRight') next.x += step;
    if (event.key.toLowerCase() === 'q') next.angle -= 5;
    if (event.key.toLowerCase() === 'e') next.angle += 5;
    const radians = next.angle * Math.PI / 180;
    const start = [next.x - Math.cos(radians) * 220, next.y - Math.sin(radians) * 220];
    const end = [next.x + Math.cos(radians) * 220, next.y + Math.sin(radians) * 220];
    const split = splitPolygon(challenge.polygon, start, end);
    if (event.key === 'Enter' || event.key === ' ') {
      setPreview(null); setKeyboard(null);
      if (split.valid) onCut(split); else onInvalid();
    } else { setKeyboard(next); setPreview({ start, end, split }); }
  };
  const line = preview && (() => {
    const { start, end } = preview, length = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const dx = (end[0] - start[0]) / length, dy = (end[1] - start[1]) / length;
    return { x1: start[0] - dx * 600, y1: start[1] - dy * 600, x2: end[0] + dx * 600, y2: end[1] + dy * 600 };
  })();
  const marker = preview?.split.valid ? markerPoint(preview.split.selected) : null;
  return <div className={`slice-surface ${result ? 'is-sliced' : ''}`}>
    <svg ref={svgRef} viewBox="0 0 360 320" className="game-svg" role="application" aria-label="Slice the shape. Drag to highlight your slice, then release. Keyboard: arrow keys move, Q and E rotate, Enter cuts." aria-disabled={disabled} tabIndex={result || disabled ? -1 : 0} data-testid="game-surface" data-seed={challenge.seed} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={event => { if (gesture.current?.id === event.pointerId) clearGesture(); }} onLostPointerCapture={event => { if (gesture.current?.id === event.pointerId) clearGesture(); }} onKeyDown={keyDown} onFocus={e => { if (e.currentTarget.matches(':focus-visible')) setKeyboardHint(true); }} onBlur={() => setKeyboardHint(false)} onContextMenu={e => e.preventDefault()}>
      <defs>
        <linearGradient id={`${id}-fill`} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={color.light}/><stop offset="100%" stopColor={color.main}/></linearGradient>
        <filter id={`${id}-shadow`} x="-40%" y="-40%" width="180%" height="190%"><feDropShadow dx="0" dy="9" stdDeviation="9" floodColor={color.edge} floodOpacity="0.16"/></filter>
        <pattern id={`${id}-hatch`} width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(35)"><path d="M0 0V9" stroke="white" strokeWidth="1" opacity=".3"/></pattern>
        <marker id={`${id}-arrow`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M0 0L10 5L0 10" fill="none" stroke="#202c31" strokeWidth="1.6"/></marker>
      </defs>
      <g className="field-crosses" aria-hidden="true"><path d="M19 20h8m-4-4v8M333 20h8m-4-4v8M19 300h8m-4-4v8M333 300h8m-4-4v8"/></g>
      <SliceShape id={id} color={color} path={path} result={result} preview={preview}/>
      {line && <g pointerEvents="none" data-testid="live-cut-line">
        <line {...line} stroke="white" strokeWidth="4.5"/>
        <line {...line} stroke="#202c31" strokeWidth="1.35" strokeDasharray="5 4"/>
        <circle cx={preview.start[0]} cy={preview.start[1]} r="3.5" fill="#fff" stroke="#202c31" strokeWidth="1.5"/>
        <line x1={preview.start[0]} y1={preview.start[1]} x2={preview.end[0]} y2={preview.end[1]} stroke="#202c31" strokeWidth="1.6" markerEnd={`url(#${id}-arrow)`}/>
      </g>}
      {marker && preview.split.percent > 12 && <text x={marker[0]} y={marker[1]} textAnchor="middle" dominantBaseline="middle" className="slice-label" pointerEvents="none">YOUR SLICE</text>}
    </svg>
    {keyboardHint && !result && <span className="keyboard-hint">Arrows move · Q/E rotate · Enter cuts</span>}
    {children}
  </div>;
}
